// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Code generated by Microsoft (R) AutoRest Code Generator.
// Changes may cause incorrect behavior and will be lost if the code is regenerated.

import type { PagedAsyncIterableIterator, PageSettings } from "@azure/core-paging";
import type { PercentileSourceTarget } from "../operationsInterfaces/index.js";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers.js";
import * as Parameters from "../models/parameters.js";
import type { CosmosDBManagementClient } from "../cosmosDBManagementClient.js";
import type {
  PercentileMetric,
  PercentileSourceTargetListMetricsOptionalParams,
  PercentileSourceTargetListMetricsResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Class containing PercentileSourceTarget operations. */
export class PercentileSourceTargetImpl implements PercentileSourceTarget {
  private readonly client: CosmosDBManagementClient;

  /**
   * Initialize a new instance of the class PercentileSourceTarget class.
   * @param client Reference to the service client
   */
  constructor(client: CosmosDBManagementClient) {
    this.client = client;
  }

  /**
   * Retrieves the metrics determined by the given filter for the given account, source and target
   * region. This url is only for PBS and Replication Latency data
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param sourceRegion Source region from which data is written. Cosmos DB region, with spaces between
   *                     words and each word capitalized.
   * @param targetRegion Target region to which data is written. Cosmos DB region, with spaces between
   *                     words and each word capitalized.
   * @param filter An OData filter expression that describes a subset of metrics to return. The
   *               parameters that can be filtered are name.value (name of the metric, can have an or of multiple
   *               names), startTime, endTime, and timeGrain. The supported operator is eq.
   * @param options The options parameters.
   */
  public listMetrics(
    resourceGroupName: string,
    accountName: string,
    sourceRegion: string,
    targetRegion: string,
    filter: string,
    options?: PercentileSourceTargetListMetricsOptionalParams,
  ): PagedAsyncIterableIterator<PercentileMetric> {
    const iter = this.listMetricsPagingAll(
      resourceGroupName,
      accountName,
      sourceRegion,
      targetRegion,
      filter,
      options,
    );
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listMetricsPagingPage(
          resourceGroupName,
          accountName,
          sourceRegion,
          targetRegion,
          filter,
          options,
          settings,
        );
      },
    };
  }

  private async *listMetricsPagingPage(
    resourceGroupName: string,
    accountName: string,
    sourceRegion: string,
    targetRegion: string,
    filter: string,
    options?: PercentileSourceTargetListMetricsOptionalParams,
    _settings?: PageSettings,
  ): AsyncIterableIterator<PercentileMetric[]> {
    let result: PercentileSourceTargetListMetricsResponse;
    result = await this._listMetrics(
      resourceGroupName,
      accountName,
      sourceRegion,
      targetRegion,
      filter,
      options,
    );
    yield result.value || [];
  }

  private async *listMetricsPagingAll(
    resourceGroupName: string,
    accountName: string,
    sourceRegion: string,
    targetRegion: string,
    filter: string,
    options?: PercentileSourceTargetListMetricsOptionalParams,
  ): AsyncIterableIterator<PercentileMetric> {
    for await (const page of this.listMetricsPagingPage(
      resourceGroupName,
      accountName,
      sourceRegion,
      targetRegion,
      filter,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Retrieves the metrics determined by the given filter for the given account, source and target
   * region. This url is only for PBS and Replication Latency data
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param sourceRegion Source region from which data is written. Cosmos DB region, with spaces between
   *                     words and each word capitalized.
   * @param targetRegion Target region to which data is written. Cosmos DB region, with spaces between
   *                     words and each word capitalized.
   * @param filter An OData filter expression that describes a subset of metrics to return. The
   *               parameters that can be filtered are name.value (name of the metric, can have an or of multiple
   *               names), startTime, endTime, and timeGrain. The supported operator is eq.
   * @param options The options parameters.
   */
  private _listMetrics(
    resourceGroupName: string,
    accountName: string,
    sourceRegion: string,
    targetRegion: string,
    filter: string,
    options?: PercentileSourceTargetListMetricsOptionalParams,
  ): Promise<PercentileSourceTargetListMetricsResponse> {
    return this.client.sendOperationRequest(
      {
        resourceGroupName,
        accountName,
        sourceRegion,
        targetRegion,
        filter,
        options,
      },
      listMetricsOperationSpec,
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const listMetricsOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.DocumentDB/databaseAccounts/{accountName}/sourceRegion/{sourceRegion}/targetRegion/{targetRegion}/percentile/metrics",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PercentileMetricListResult,
    },
  },
  queryParameters: [Parameters.apiVersion, Parameters.filter],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.accountName,
    Parameters.sourceRegion,
    Parameters.targetRegion,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
